import { useState, useRef, useEffect } from 'react';
import { Sun, Moon, Monitor, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTheme, Theme } from '@hooks/index';
import { cn } from '@utils/cn';

const THEME_OPTIONS: { value: Theme; icon: typeof Sun }[] = [
    { value: 'light', icon: Sun },
    { value: 'dark', icon: Moon },
    { value: 'system', icon: Monitor },
];

interface ThemeToggleProps {
    className?: string;
}

export function ThemeToggle({ className }: ThemeToggleProps) {
    const { t } = useTranslation();
    const { theme, setTheme, isDark } = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Close menu on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const CurrentIcon = theme === 'light' ? Sun : theme === 'dark' ? Moon : Monitor;

    return (
        <div ref={menuRef} className={cn('relative', className)}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={cn(
                    'p-2 rounded-lg transition-colors',
                    isDark 
                        ? 'text-dark-400 hover:text-white hover:bg-white/5' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                )}
                title={t('theme.title')}
                aria-label={t('theme.title')}
            >
                <CurrentIcon size={20} />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className={cn(
                            'absolute right-0 mt-2 w-40 py-2 rounded-xl border shadow-xl z-50',
                            isDark 
                                ? 'bg-dark-50 border-dark-200' 
                                : 'bg-white border-gray-200'
                        )}
                    >
                        <div className={cn(
                            'px-3 py-1.5 text-xs font-medium',
                            isDark ? 'text-dark-500' : 'text-gray-500'
                        )}>
                            {t('theme.title')}
                        </div>
                        {THEME_OPTIONS.map(({ value, icon: Icon }) => (
                            <button
                                key={value}
                                onClick={() => {
                                    setTheme(value);
                                    setIsOpen(false);
                                }}
                                className={cn(
                                    'w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors',
                                    isDark 
                                        ? 'hover:bg-white/5' 
                                        : 'hover:bg-gray-100',
                                    theme === value
                                        ? isDark ? 'text-white' : 'text-gray-900'
                                        : isDark ? 'text-dark-400' : 'text-gray-600'
                                )}
                            >
                                <Icon size={16} />
                                <span className="flex-1 text-left">{t(`theme.${value}`)}</span>
                                {theme === value && (
                                    <Check size={14} className="text-primary" />
                                )}
                            </button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default ThemeToggle;
